import React, { useContext, useEffect, useMemo, useState } from 'react';
import styles from './AddCategoryForm.module.scss';
import { CloseRoundSmall, Input, Magnifier } from 'esa-ui-library';
import { Form } from '../../../components';
import { findItemInTree, getAllOptions, searchNestedTree, useStore } from '../../../../common';
import { PropertyType, TreeValueType } from 'esa-ui-library/lib/types';
import { AuthContext } from '../../../../logic';
import treeStyles from 'esa-ui-library/lib/components/TreeSelect/TreeSelect.module.scss';
import { TreeSelectOptions } from 'esa-ui-library/lib/components/TreeSelectOptions/TreeSelectOptions';
import { Chips } from 'esa-ui-library/lib/components/Chips/Chips';
import { ClickDetector } from 'esa-ui-library/lib/components/ClickDetector/ClickDetector';
import { ArrowDown } from 'esa-ui-library/lib/assets/icons';

interface AssignPartnerFormProps {
	onValidityChange?: (isValid: boolean) => void;
}

const AssignPartnersForm = ({ onValidityChange }: AssignPartnerFormProps): JSX.Element => {
	const [state, dispatch] = useStore();
	const [isLoading, setIsLoading] = useState(true);
	const { token } = useContext(AuthContext);
	const { success, selectedGame, partners, activePartners, gameData } = state;
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [opened, setOpened] = useState<string[]>([]);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const getGame = async () => {
		setIsLoading(true);
		await dispatch('GAME_INIT', { token, id: selectedGame.internal_game_id });
		setIsLoading(false);
	};

	const onPartnersChange = (val: PropertyType[]) => {
		const flattened: PropertyType[] = [];

		// Identify which item was just toggled (added or removed)
		const currentValues = activePartners.map((p: PropertyType) => p.value);
		const newValues = val.map((v: PropertyType) => v.value);

		const added = val.find((v) => !currentValues.includes(v.value));
		const removed = activePartners.find((p: PropertyType) => !newValues.includes(p.value));

		const changedItem = added || removed;

		if (changedItem) {
			const group = findItemInTree(filteredPartners, changedItem.value);
			if (group?.options) {
				const groupOptions = getAllOptions(group);
				const groupOptionValues = groupOptions.map((opt: PropertyType) => opt.value);
				const allSelected = groupOptionValues.every((v: any) => currentValues.includes(v));

				let nextActivePartners: PropertyType[] = [...activePartners];

				if (allSelected) {
					// Clear all from this group
					nextActivePartners = nextActivePartners.filter((p) => !groupOptionValues.includes(p.value));
				} else {
					// Select all from this group
					groupOptions.forEach((opt: PropertyType) => {
						if (!nextActivePartners.find((f: PropertyType) => f.value === opt.value)) {
							nextActivePartners.push(opt);
						}
					});
				}

				dispatch('PARTNERS_UPDATE', nextActivePartners);
				dispatch('FORM_GAME_PARTNERS', { activePartners: nextActivePartners });
				onValidityChange?.(nextActivePartners.length > 0);
				return;
			}
		}

		val.forEach((item: PropertyType) => {
			const group = findItemInTree(filteredPartners, item.value);
			if (group?.options) {
				const groupOptions = getAllOptions(group);
				groupOptions.forEach((opt: PropertyType) => {
					if (!flattened.find((f: PropertyType) => f.value === opt.value)) {
						flattened.push(opt);
					}
				});
			} else {
				// Find the full item in the tree to ensure we have all its properties
				const fullItem = findItemInTree(filteredPartners, item.value) || item;

				if (!flattened.find((f: PropertyType) => f.value === fullItem.value)) {
					flattened.push(fullItem);
				}
			}
		});

		dispatch('PARTNERS_UPDATE', flattened);
		dispatch('FORM_GAME_PARTNERS', { activePartners: flattened });
		onValidityChange?.(Array.isArray(flattened) && flattened.length > 0);
	};

	const onClear = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		dispatch('PARTNERS_UPDATE', []);
		dispatch('FORM_GAME_PARTNERS', { activePartners: [] });
		onValidityChange?.(false);
	};

	useEffect(() => {
		if (selectedGame) {
			getGame();
		}
	}, [selectedGame?.internal_game_id]);

	useEffect(() => {
		if (Array.isArray(gameData?.assigned_partners) && gameData?.assigned_partners.length > 0 && selectedGame) {
			const preselected = gameData.assigned_partners.map((p: any) => ({
				label: p.name,
				value: p.gid,
				skinId: p.partner_id,
			}));

			dispatch('PARTNERS_UPDATE', preselected);
			dispatch('FORM_GAME_PARTNERS', { activePartners: preselected });
			onValidityChange?.(true);
		} else if (selectedGame) {
			dispatch('PARTNERS_UPDATE', []);
			dispatch('FORM_GAME_PARTNERS', { activePartners: [] });
			onValidityChange?.(false);
		}
	}, [gameData?.assigned_partners, selectedGame]);

	const filteredPartners = useMemo(() => {
		if (!searchTerm) return partners;
		return searchNestedTree(partners, searchTerm);
	}, [partners, searchTerm]);

	const renderOptions = useMemo(
		() => (
			<section className={treeStyles.OptionWrapper}>
				<div className={styles.SearchContainer}>
					<Input
						type='text'
						value={searchTerm}
						onChange={setSearchTerm}
						icon={<Magnifier />}
						placeholder='Search partners'
						small
					/>
					{searchTerm && <CloseRoundSmall className={styles.Clear} onClick={() => setSearchTerm('')} />}
				</div>
				<div className={styles.List}>
					<TreeSelectOptions
						options={filteredPartners}
						selected={activePartners}
						opened={opened}
						onChange={onPartnersChange}
						onOpenedChange={setOpened}
					/>
					{filteredPartners?.length === 0 && <p className={styles.EmptyList}>No results</p>}
				</div>
			</section>
		),
		[filteredPartners, activePartners, opened, searchTerm, onPartnersChange]
	);

	return (
		<section className={isLoading ? styles.DisableForm : ''}>
			<Form success={success}>
				<div>
					<div className={`${treeStyles.TreeSelectContainer} ${styles.PartnerSelect}`}>
						<label className={treeStyles.Label}>
							Partners
							<span className={treeStyles.Required}>*</span>
						</label>
						<ClickDetector
							onOutsideClick={() => {
								setIsOpen(false);
								setSearchTerm('');
							}}
							dataId='detector'
						>
							<div
								className={treeStyles.TreeSelectWrapper}
								onClick={() => !isOpen && setIsOpen(true)}
								data-testid='partners-field'
							>
								<div className={`${treeStyles.TreeSelect} ${isOpen ? treeStyles.Active : ''}`}>
									{!!activePartners?.length && (
										<>
											<Chips
												options={activePartners as TreeValueType[]}
												onChange={onPartnersChange}
												removeable
												primary
												limit={1}
											/>
											<div className={styles.Clear} onClick={onClear}>
												<CloseRoundSmall />
											</div>
										</>
									)}
									{!activePartners?.length && <span className={treeStyles.Placeholder}>Select partners</span>}
									<i className={treeStyles.Icon}>
										<ArrowDown />
									</i>
								</div>
								{isOpen && (
									<div className={treeStyles.OptionContainer} data-testid='options'>
										{renderOptions}
									</div>
								)}
							</div>
						</ClickDetector>
					</div>
				</div>
			</Form>
		</section>
	);
};

export { AssignPartnersForm };
